import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaBanner } from "@/components/CtaBanner";
import { getArea, services, SITE_URL } from "@/data/seo";

export const AreaPage = () => {
  const { areaSlug } = useParams();
  const a = getArea(areaSlug);
  if (!a) return <Navigate to="/areas" replace />;
  const canonical = `/areas/${a.slug}`;

  const jsonld = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `BoxB Solutions — ${a.name}`,
    url: `${SITE_URL}${canonical}`,
    telephone: "+447570291363",
    email: "info@boxb.uk",
    address: { "@type": "PostalAddress", addressLocality: a.name, addressRegion: "South Wales", addressCountry: "GB" },
    areaServed: [a.name, ...a.landmarks],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "127" },
  };

  return (
    <main className="bg-white min-h-screen">
      <Seo title={a.metaTitle} description={a.metaDescription} canonical={canonical} jsonld={jsonld} />
      <Nav />

      <section className="pt-28 md:pt-32 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Breadcrumbs trail={[
            { href: "/", label: "Home" },
            { href: "/areas", label: "Areas" },
            { href: canonical, label: a.name },
          ]} />

          <span className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-bold text-[#2563EB]">
            <MapPin className="h-4 w-4" /> Serving {a.name}
          </span>
          <h1 className="mt-4 font-display font-bold text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            House Removals, Office Moves &amp; Cleaning in {a.name}
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-3xl">{a.lead}</p>

          <div className="mt-10 rounded-3xl bg-[#F8FAFC] border border-slate-200 p-8">
            <h2 className="font-display font-semibold text-xl text-[#0A192F]">Neighbourhoods we cover in {a.name}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {a.landmarks.map((l) => (
                <span key={l} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-sm text-[#0A192F]">
                  <MapPin className="h-3.5 w-3.5 text-[#2563EB]" /> {l}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-display font-semibold text-3xl text-[#0A192F] tracking-tight">
            Every service you need in {a.name}
          </h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.values(services).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}/${a.slug}`}
                    className="group rounded-3xl bg-white border border-slate-200 p-7 hover-lift hover:border-[#0A192F] transition-colors">
                <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#2563EB]">{s.tag}</span>
                <h3 className="mt-2 font-display font-semibold text-xl text-[#0A192F]">
                  {s.shortName} {a.name}
                </h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">{s.metaDescription}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0A192F] group-hover:text-[#2563EB]">
                  Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner title={`Book your ${a.name} move today`} subtitle={`Free quote in 15 minutes. Fully insured. Serving ${a.name} 7 days a week.`} />

      <Footer />
      <StickyCTA />
    </main>
  );
};
